import { clsx } from 'clsx'
import { type FC, type ReactNode, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconArrowRight, IconHelp } from '@redteclab/icons'

import { Button, Divider } from 'base-ui'

import {
  FEATURE_TOGGLE_REDPOINTS_ACTIVE,
  useFeatureToggleContext,
} from '../../../../../../feature-toggles'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../../../global-config'
import {
  urlResolverGetAccount,
  urlResolverGetAccountNotepad,
  urlResolverGetAccountOrders,
  urlResolverGetAccountRedpoints,
  urlResolverGetContactUs,
} from '../../../../../../url-handling'

const getAccountLinks = (
  globalConfig: PublicGlobalConfig,
  isRedpointsActive: boolean,
  isLoggedIn: boolean,
): { content: ReactNode; key: string }[] => {
  const accountLinks = [
    {
      content: (
        <Button
          className="w-full justify-between"
          endIcon={<IconArrowRight />}
          href={urlResolverGetAccount(globalConfig)}
          size="large"
          variant="text"
        >
          <FormattedMessage id="menubar.AccountFlyout.link.MyOverview" />
        </Button>
      ),
      key: 'header-account-overview',
    },
    {
      content: (
        <Button
          className="w-full justify-between"
          endIcon={<IconArrowRight />}
          href={urlResolverGetAccountOrders(globalConfig)}
          size="large"
          variant="text"
        >
          <FormattedMessage id="menubar.AccountFlyout.link.MyOrdersAndInvoices" />
        </Button>
      ),
      key: 'header-account-orders',
    },
    {
      content: (
        <Button
          className="w-full justify-between"
          endIcon={<IconArrowRight />}
          href={urlResolverGetAccountNotepad(globalConfig)}
          size="large"
          variant="text"
        >
          <FormattedMessage id="general.label.wishlist" />
        </Button>
      ),
      key: 'header-account-notepad',
    },
  ]

  if (isRedpointsActive) {
    accountLinks.push({
      content: (
        <Button
          className="w-full justify-between"
          endIcon={<IconArrowRight />}
          href={urlResolverGetAccountRedpoints(globalConfig)}
          size="large"
          variant="text"
        >
          <FormattedMessage id="menubar.AccountFlyout.link.MyRedpoints" />
        </Button>
      ),
      key: 'header-account-redpoints',
    })
  }

  accountLinks.push({
    content: (
      <div className="text-center">
        <Divider />
        <Button
          className={clsx('mt-4', isLoggedIn ? 'mb-4' : 'mb-0')}
          href={urlResolverGetContactUs(globalConfig)}
          size="large"
          startIcon={<IconHelp className="text-dark-brand" />}
          variant="text"
        >
          <FormattedMessage id="header.link.label.contactAndHelp" />
        </Button>
        {isLoggedIn ? <Divider /> : null}
      </div>
    ),
    key: 'header-account-contact-us',
  })

  return accountLinks
}

export const HeaderMyAccountLinks: FC<{ isLoggedIn: boolean }> = ({
  isLoggedIn,
}) => {
  const globalConfig = useGlobalConfigContext()
  const featureToggles = useFeatureToggleContext()

  const isRedpointsActive = Boolean(
    featureToggles[FEATURE_TOGGLE_REDPOINTS_ACTIVE],
  )

  const accountLinks = useMemo(
    () => getAccountLinks(globalConfig, isRedpointsActive, isLoggedIn),
    [globalConfig, isRedpointsActive, isLoggedIn],
  )

  return (
    <ul>
      {accountLinks.map(({ content, key }) => (
        <li
          className="mb-4 whitespace-nowrap last:mb-0"
          data-qa-id={key}
          key={key}
        >
          {content}
        </li>
      ))}
    </ul>
  )
}
